import {RootStore} from './RootStore';
import {fetchProduct} from '../services/product-service/fetchProduct';
import {RootStoreState} from './RootStoreState';
import {getReferringPage} from '@wix/wixstores-client-storefront-sdk/dist/es/src/utils/sitemap/sitemapUtils';
import {SPECS} from '../../../specs';
import {fetchClientRenderingData} from './data/fetchClientRenderingData';
import {fetchServerRenderingSafeData} from './data/fetchServerRenderingSafeData';
import {withWarmup} from './data/withWarmup';
import {fetchDeferredData} from './data/fetchDeferredData';

const getInitialData = async (rootStore: RootStore, slug: string) => {
  const [
    product,
    {isCollectingRequests: isCollectingBackInStockRequests},
    contextualProducts,
    storeSettings,
    siteStructure,
    referringPageId,
    referringPagePath,
  ] = await Promise.all([
    fetchProduct(rootStore, slug), // TODO:Zeev: fetchProduct &  fetchBackInStockSettings are both gql calls that can be merged into one call
    rootStore.ecomPublicApi.backInStock.fetchBackInStockSettings(),
    rootStore.ecomPublicApi.product.getContextualProducts(),
    rootStore.ecomPublicApi.settings.getSettings(),
    rootStore.wixCodeApi.site.getSiteStructure({includePageId: true}),
    rootStore.ecomPublicApi.product.getReferringPageId(),
    rootStore.ecomPublicApi.product.getReferringPagePath(),
  ]);

  if (!product) {
    return null;
  }

  const absoluteProductUrl = await rootStore.ecomPublicApi.product.getAbsoluteProductUrl({slug: product.urlPart});
  const referringPage = await getReferringPage(rootStore.wixCodeApi, {
    pageId: referringPageId as string,
    pagePath: referringPagePath,
  });
  return {
    product,
    isCollectingBackInStockRequests,
    contextualProducts,
    storeSettings,
    absoluteProductUrl,
    siteStructure,
    referringPage,
  };
};

const getServerRenderingSafeData = async (rootStore: RootStore, slug: string) => {
  return withWarmup(rootStore, slug, () => fetchServerRenderingSafeData(rootStore, slug));
};

const getClientRenderingOnlyData = async (
  rootStore: RootStore,
  {shouldFetchDeferredOnInit}: {shouldFetchDeferredOnInit: boolean},
) => {
  const {flowAPI} = rootStore.controllerParams;
  if (!flowAPI.environment.isSSR) {
    const clientData = await fetchClientRenderingData(rootStore);
    if (shouldFetchDeferredOnInit) {
      const deferredData = await fetchDeferredData(rootStore, clientData);
      return Object.assign(clientData, deferredData);
    }
    return clientData;
  }
  return {};
};

export const createInitialState = async (
  rootStore: RootStore,
  slug: string,
  {shouldFetchDeferredOnInit = false}: {shouldFetchDeferredOnInit?: boolean} = {},
): Promise<RootStoreState | undefined> => {
  const {warmupData} = rootStore.controllerParams.controllerConfig.wixCodeApi.window;
  const {flowAPI} = rootStore.controllerParams;
  if (rootStore.isExperimentEnabled(SPECS.UpdateBreadcrumbsAfterHydration)) {
    const serverSafeData = await getServerRenderingSafeData(rootStore, slug);
    const clientOnlyData = await getClientRenderingOnlyData(rootStore, {shouldFetchDeferredOnInit});
    if (serverSafeData) {
      return new RootStoreState(rootStore, {...serverSafeData, ...clientOnlyData});
    }
  } else {
    if (flowAPI.environment.isSSR) {
      warmupData.set(slug, await getInitialData(rootStore, slug));
    }

    const initialDataFromWarmup = warmupData.get(slug);
    const initialData = initialDataFromWarmup || (await getInitialData(rootStore, slug));
    if (initialData) {
      return new RootStoreState(rootStore, initialData);
    }
  }
};
